import React from 'react';
import { Column } from 'react-table';
import { FormattedMessage } from 'react-intl';
import Highlighter from 'react-highlight-words';
import { IPosition } from '../../../../common/models/position/IPosition';
import { PositionFilterType } from '../../../../common/enums/PositionFilterType';
import { ICheckboxFilterItem } from '../../../../common/models/filter/ICheckboxFilterItem';
import { SortType } from '../../../../common/enums/SortType';
import { formatDate } from '../../../../common/helpers/dateHelper';
import CustomBadge from '../../../../components/CustomBadge';
import { DashboardColor } from '../../../../common/enums/DashboardColor';
import styles from './styles.module.scss';
import TextOverflowTooltip from '../../../../components/TextOverflowTooltip';

const getProgress = (item: IPosition) => {
  if (!item.activeCandidates) {
    return (
      <CustomBadge min={1} value={0} max={1}>
        <FormattedMessage id="inactive" defaultMessage="Inactive" />
      </CustomBadge>
    );
  }
  if (item.activeCandidates === item.completedCandidates) {
    return (
      <CustomBadge min={0} value={1} max={1}>
        <FormattedMessage id="All Candidates Completed" defaultMessage="All Candidates Completed" />
      </CustomBadge>
    );
  }
  if (!item.completedCandidates) {
    return (
      <CustomBadge min={1} value={0} max={1}>
        <FormattedMessage id="No Candidates Completed" defaultMessage="No Candidates Completed" />
      </CustomBadge>
    );
  }
  return (
    <CustomBadge min={1} value={item.completedCandidates} max={item.activeCandidates}>
      <FormattedMessage id="Candidates Completed" defaultMessage="Candidates Completed">
        {message => `${item.completedCandidates} / ${item.activeCandidates} ${message}`}
      </FormattedMessage>
    </CustomBadge>
  );
};

const candidatesCell = (value: number, maxWidth: string) => (
  <div style={{ maxWidth }} className="table-center">
    <CustomBadge min={1} value={0} max={1} className={`${styles.candidates} t-14`}>
      {value}
    </CustomBadge>
  </div>
);

export const getColumns = (search: string): Column[] => [
  {
    Header: () => <FormattedMessage id="positionTable.requestDate" defaultMessage="Request date" />,
    accessor: 'lastModified',
    width: 135,
    Cell: ({ cell: { value } }) => formatDate(value),
    sortType: SortType.Date
  },
  {
    Header: () => <FormattedMessage id="positionTable.positionTitle" defaultMessage="Position title" />,
    id: 'name',
    Cell: ({ row: { original } }: any) => (
      <TextOverflowTooltip content={original.name} width={240}>
        <Highlighter
          highlightClassName="highlighted-text"
          searchWords={[search]}
          textToHighlight={original.name}
          className="cell-active"
          autoEscape
        />
      </TextOverflowTooltip>
    ),
    width: 200
  },
  {
    Header: () => <FormattedMessage id="positionTable.candidateProgress" defaultMessage="Candidate progress" />,
    accessor: (item: any) => getProgress(item),
    id: 'progress',
    sortType: SortType.Numeric,
    disableFilters: true,
    width: 200
  },
  {
    Header: () => <FormattedMessage id="positionTable.activeCandidates" defaultMessage="Active candidates" />,
    accessor: 'activeCandidates',
    Cell: ({ cell: { value } }) => candidatesCell(value, '120px'),
    sortType: SortType.Numeric,
    disableFilters: true
  },
  {
    Header: () => <FormattedMessage id="positionTable.allCandidates" defaultMessage="All candidates" />,
    accessor: 'lifetimeCandidates',
    Cell: ({ cell: { value } }) => candidatesCell(value, '90px'),
    sortType: SortType.Numeric,
    disableFilters: true
  }
];

export const sidebarItems: ICheckboxFilterItem[] = [{
  id: PositionFilterType.AllActivePositions,
  name: 'positionSidebarFilter.AllActivePositions',
  color: DashboardColor.Neutral,
  count: 0,
  children: [{
    id: PositionFilterType.NotStarted,
    name: 'positionSidebarFilter.Incomplete',
    color: DashboardColor.BrightNeutral,
    count: 0
  } as ICheckboxFilterItem,
  {
    id: PositionFilterType.InProcess,
    name: 'positionSidebarFilter.PartiallyComplete',
    color: DashboardColor.Yellow,
    count: 0
  } as ICheckboxFilterItem,
  {
    id: PositionFilterType.Complete,
    name: 'positionSidebarFilter.Complete',
    color: DashboardColor.Green,
    count: 0
  } as ICheckboxFilterItem]
} as ICheckboxFilterItem,
{
  id: PositionFilterType.InactivePositions,
  name: 'positionSidebarFilter.InactivePositions',
  color: DashboardColor.Neutral,
  count: 0
} as ICheckboxFilterItem,
{
  id: PositionFilterType.AllPositions,
  name: 'positionSidebarFilter.AllPositions',
  color: DashboardColor.Blue,
  count: 0,
  deselectAll: true
} as ICheckboxFilterItem
];
